import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppService } from './app.service';
import { messageService } from './message.service';

export interface TrayPrinter {
  printerNo: string;
  printerName: string;
  displayName: string;
  printerLocation: string;
}

@Injectable({ providedIn: 'root' })
export class OneQmsPrinterService {

  private readonly TRAY_URL = 'https://oneqms-printer';

  private currentToken = '';
  private tokenExpiry = 0;

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private readonly message: messageService
  ) { }

  private get backendUrl(): string {
    return this.appService.baseUrl;
  }

  // ── Token management ──────────────────────────────────────────────────

  private async getValidToken(forceFresh = false): Promise<string> {
    const now = Date.now();

    if (!forceFresh && this.currentToken && now < this.tokenExpiry - 60_000) {
      return this.currentToken;
    }

    this.currentToken = '';
    this.tokenExpiry = 0;

    // ── Step 1a: Fast Fetch siteId via Focused Payload (Using correct context mapping path) ───
    let siteId = localStorage.getItem('siteId') || 'SITE_DEFAULT';
    try {
      // Directing lookups across configuration mappings matching backend routing rules
      const siteConfig: any = await firstValueFrom(
        this.http.get(`${this.backendUrl}/dmsGroupUsers/userSite`)
      );
      if (siteConfig?.siteId) {
        siteId = siteConfig.siteId;
      }
    } catch (err: any) {
      console.warn('[OneQmsPrinter] Fast siteId lookup failed — falling back to context defaults.', err);
    }

    // ── Step 1b: Fetch tray config (Loader Enabled) ───────────────────────
    let traySecret: string;
    let expectedThumbprint: string;

    try {
      const config: any = await firstValueFrom(
    this.http.get(
        `${this.backendUrl}/trayCaCertificates/${siteId}/trayConfig`
    )
);

      traySecret = config?.trayAuthSecret;
      expectedThumbprint = config?.caCertThumbprint;

      if (!traySecret) {
        throw new Error(
          'Tray not configured. Run OneQMS Print Tray setup wizard on this machine.');
      }

      // ── [H1] Cert pinning — MANDATORY, not optional ─────────────────────
      if (!expectedThumbprint) {
        throw new Error(
          'Security Error: Printer certificate could not be verified.\n' +
          'Contact your administrator. (Thumbprint not configured in DB)'
        );
      }

      // Fetch CA cert thumbprint from local tray
      let trayThumbprint: string;
      try {
        const trayCert: any = await firstValueFrom(
          this.http.get(`${this.TRAY_URL}/api/cert-info`)
        );
        trayThumbprint = trayCert?.thumbprint;

        if (!trayThumbprint) {
          throw new Error(
            'Tray returned no certificate thumbprint. ' +
            'Re-run the Print Tray setup wizard.');
        }
      } catch (certErr: any) {
        if (certErr?.status === 0) {
          throw new Error(
            'Cannot connect to Print Tray for certificate verification.\n\n' +
            'Open https://oneqms-printer/api/health in Chrome, ' +
            'accept the certificate, then try again.');
        }
        throw new Error(
          certErr?.message || 'Could not verify printer security certificate.');
      }

      // Compare — BLOCK on mismatch
      if (trayThumbprint !== expectedThumbprint) {
        console.error(
          `[OneQmsPrinter] CERT MISMATCH ❌\n` +
          `  Expected (DB):  ${expectedThumbprint}\n` +
          `  Got (Tray):     ${trayThumbprint}`
        );
        throw new Error(
          '🔒 Security Alert: Printer certificate mismatch detected.\n' +
          'The printer connection may not be secure. Printing blocked.\n' +
          'Contact your administrator.'
        );
      }

      console.log('[OneQmsPrinter] ✅ Cert pinned | thumbprint='
        + trayThumbprint.substring(0, 16) + '...');

    } catch (err: any) {
      if (err?.status === 404) {
        throw new Error(
          'OneQMS Print Tray not set up for this site.\n' +
          'Run the OneQMS Print Tray installer on this machine first.');
      }
      throw err;
    }

    // ── Step 2: Authenticate with local tray ─────────────────────────────
    try {
      const res: any = await firstValueFrom(
        this.http.post(
          `${this.TRAY_URL}/api/auth/token`,
          {
            username: this.message.getOrgId(),
            sessionId: 'session-' + Date.now()
          },
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              'X-Tray-Auth-Secret': traySecret
            })
          }
        )
      );

      this.currentToken = res?.token || res?.accessToken || res?.jwt || '';
      const expiresIn = res?.expiresIn || 3600;
      this.tokenExpiry = now + expiresIn * 1000;

      if (!this.currentToken) {
        throw new Error('Tray returned no token: ' + JSON.stringify(res));
      }

      console.log('[OneQmsPrinter] Token ready | expiresIn=' + expiresIn + 's');
      return this.currentToken;

    } catch (err: any) {
      this.currentToken = '';
      this.tokenExpiry = 0;

      if (err?.status === 0) {
        throw new Error(
          'Cannot connect to OneQMS Print Tray.\n\n' +
          'Please do this once in Chrome:\n' +
          '1. Open https://oneqms-printer/api/health\n' +
          '2. Click "Advanced" → "Proceed to oneqms-printer (unsafe)"\n' +
          '3. Then try printing again.'
        );
      }
      if (err?.status === 401) {
        throw new Error(
          'Tray authentication failed — secret mismatch. Re-run setup wizard.');
      }
      throw err;
    }
  }

  private buildAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  // ── Public API ────────────────────────────────────────────────────────

  public async isTrayRunning(): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.get(`${this.TRAY_URL}/api/health`, { responseType: 'text' })
      );
      return true;
    } catch {
      return false;
    }
  }

  public async getPrinters(): Promise<TrayPrinter[]> {
    const token = await this.getValidToken(false);
    const res: any = await firstValueFrom(
      this.http.get(
        `${this.TRAY_URL}/api/printers`,
        { headers: this.buildAuthHeaders(token) }
      )
    );
    console.log('[OneQmsPrinter] Printers loaded:', res?.printers?.length);
    return (res?.printers || []) as TrayPrinter[];
  }

  public async printPdf(
    printer: TrayPrinter,
    base64Pdf: string,
    copies: number = 1,
    meta: {
      fileName: string;
      documentRefId: string;
      moduleName: string;
      userId: string;
      orgId: string;
      duplex?: boolean;
      orientation?: 'PORTRAIT' | 'LANDSCAPE';
    }
  ): Promise<void> {

    const token = await this.getValidToken(true);

    const result: any = await firstValueFrom(
      this.http.post(
        `${this.TRAY_URL}/api/print`,
        {
          printerName: printer.printerName,
          printerNo: printer.printerNo,
          printerLocation: printer.printerLocation,
          type: 'PDF',
          data: base64Pdf,
          copies: Math.max(1, Math.min(copies, 20)),
          fileName: meta.fileName,
          documentRefId: meta.documentRefId,
          moduleName: meta.moduleName,
          userId: meta.userId,
          orgId: meta.orgId,
          duplex: meta.duplex ? 'TWO_SIDED_LONG_EDGE' : 'ONE_SIDED',
          orientation: meta.orientation || 'PORTRAIT'
        },
        { headers: this.buildAuthHeaders(token) }
      )
    );

    console.log('[OneQmsPrinter] Printed ✅'
      + ' | printer=' + printer.printerName
      + ' | printerNo=' + printer.printerNo
      + ' | jobId=' + result?.jobId);
  }

  public async refreshPrinters(): Promise<TrayPrinter[]> {
    const token = await this.getValidToken(true); // fresh token

    const res: any = await firstValueFrom(
      this.http.post(
        `${this.TRAY_URL}/api/printers/refresh`,
        {},
        { headers: this.buildAuthHeaders(token) }
      )
    );

    console.log('[OneQmsPrinter] Refresh done | count='
      + res?.count + ' | ip=' + res?.ip);

    return ((res?.printers || []) as any[]).map((p: any) => ({
      printerNo: p.printerNo,
      printerName: p.printerName,
      displayName: p.displayName,
      printerLocation: p.printerLocation
    })) as TrayPrinter[];
  }
}
